import { useAtom } from 'jotai';
import { atom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { adminFeedbackService } from '@/services/adminFeedback.service';
import { invalidateQueries } from '@/lib/query-client';
import type { AdminFeedbackFiltersDto } from '@/types/dto.types';

const adminFeedbacksPaginationAtom = atom<{ page: number; limit: number }>({
  page: 1,
  limit: 20,
});

const adminFeedbacksFiltersAtom = atom<Omit<AdminFeedbackFiltersDto, 'page' | 'limit'>>({});

const adminFeedbacksQueryAtom = atomWithQuery((get) => {
  const { page, limit } = get(adminFeedbacksPaginationAtom);
  const filters = get(adminFeedbacksFiltersAtom);
  return {
    queryKey: ['admin-feedbacks', page, limit, filters],
    queryFn: () => adminFeedbackService.list({ ...filters, page, limit }),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  };
});

const markFeedbackAddressedMutationAtom = atomWithMutation(() => ({
  mutationKey: ['admin-feedbacks', 'mark-addressed'],
  mutationFn: ({ id, note }: { id: string; note: string }) =>
    adminFeedbackService.markAddressed(id, note),
  onSuccess: () => {
    invalidateQueries(['admin-feedbacks']);
  },
}));

const unmarkFeedbackAddressedMutationAtom = atomWithMutation(() => ({
  mutationKey: ['admin-feedbacks', 'unmark-addressed'],
  mutationFn: (id: string) => adminFeedbackService.unmarkAddressed(id),
  onSuccess: () => {
    invalidateQueries(['admin-feedbacks']);
  },
}));

export function useAdminFeedbacks() {
  const [query] = useAtom(adminFeedbacksQueryAtom);
  return query;
}

export function useAdminFeedbacksPagination() {
  return useAtom(adminFeedbacksPaginationAtom);
}

export function useAdminFeedbacksFilters() {
  return useAtom(adminFeedbacksFiltersAtom);
}

export function useMarkFeedbackAddressed() {
  const [mutation] = useAtom(markFeedbackAddressedMutationAtom);
  return mutation;
}

export function useUnmarkFeedbackAddressed() {
  const [mutation] = useAtom(unmarkFeedbackAddressedMutationAtom);
  return mutation;
}
